import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Snackbar, Tab, Tabs } from "@mui/material";
import { CSSProperties, KeyboardEvent, MouseEvent, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RecordManagement } from "app/class/record-management";
import { useColumns } from "app/hooks/useColumns";
import { useTables } from "app/hooks/useTables";
import BuilderToolbar from "./shared/BuilderToolbar";
import FieldPalette from "./nocode/FieldPalette";
import FormCanvas from "./nocode/FormCanvas";
import PropertyPanel from "./nocode/PropertyPanel";
import HtmlEditor from "./developer/HtmlEditor";
import ScriptEditor from "./developer/ScriptEditor";
import FormPreview from "./developer/FormPreview";
import { useFormBuilder } from "../hooks/useFormBuilder";
import { useTargetColumns } from "../hooks/useTargetColumns";
import { useAuth } from "app/providers/AuthProvider";
import { FormBuilderInitialMetadata, FormBuilderState } from "../types/formBuilder.types";
import { useBlocker } from "react-router-dom";
import { normalizeBuilderToken } from "../hooks/useFormBuilder";
import { parseFormHtml } from "../services/htmlParser";
import { usePageTitle } from "app/hooks/usePageTitle";
import { useTranslationsReady } from "app/providers/TranslationProvider";
import { useFormTitleTranslations } from "../services/formTitle";

import "./form-builder-shell.scss";
import queryKeys from "shared/services/queryKeys";

const LAYOUT_TABLE = "service_builder";
const LAYOUT_PREFERENCE_KEY = "service_builder.layout";
const DEFAULT_LEFT_WIDTH = 270;
const DEFAULT_RIGHT_WIDTH = 360;
const MIN_LEFT_WIDTH = 220;
const MAX_LEFT_WIDTH = 420;
const MIN_RIGHT_WIDTH = 300;
const MAX_RIGHT_WIDTH = 520;
const MIN_CANVAS_WIDTH = 480;
const MIN_FIT_LEFT_WIDTH = 150;
const MIN_FIT_RIGHT_WIDTH = 160;
const HANDLE_WIDTH = 6;

interface BuilderLayoutPreference {
    leftSidebarWidth?: number;
    rightSidebarWidth?: number;
}

const persistentSnapshot = (state: FormBuilderState) => JSON.stringify({
    formTitle: state.formTitle, formName: state.formName, formSlug: state.formSlug, styleCode: state.styleCode,
    categoryId: state.categoryId, accessType: state.accessType, accessRoles: state.accessRoles,
    accessGroupIds: state.accessGroupIds, publicAccessMode: state.publicAccessMode, active: state.active,
    published: state.published, version: state.version, requestStatusField: state.requestStatusField,
    requestStatusMappingJson: state.requestStatusMappingJson, targetTable: state.targetTable,
    onSubmit: state.onSubmit, successMessage: state.successMessage, successRedirect: state.successRedirect,
    recordId: state.recordId, eventsBinding: state.eventsBinding, fields: state.fields, steps: state.steps,
    sections: state.sections, htmlSource: state.htmlSource, scriptCode: state.scriptCode,
});

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

interface FormBuilderShellProps {
    formId?: string;
    initialHtml?: string;
    initialScriptCode?: string;
    initialMetadata?: FormBuilderInitialMetadata;
    saving?: boolean;
    onSave?: (state: FormBuilderState) => Promise<void> | void;
}

const FormBuilderShell = ({ formId, initialHtml, initialScriptCode = "", initialMetadata, saving = false, onSave }: FormBuilderShellProps) => {
    const { t } = useTranslation();
    const translationsReady = useTranslationsReady();
    const { state, actions, selectedFieldDef } = useFormBuilder(initialHtml, initialScriptCode,
        { ...initialMetadata, translationId: formId && formId !== "new" ? formId : initialMetadata?.translationId });
    const formTitle = useFormTitleTranslations([state.formTitle]);
    const displayTitle = formTitle(state.formTitle, state.formName);
    const { user } = useAuth();
    const queryClient = useQueryClient();
    const [leftSidebarWidth, setLeftSidebarWidth] = useState(DEFAULT_LEFT_WIDTH);
    const [rightSidebarWidth, setRightSidebarWidth] = useState(DEFAULT_RIGHT_WIDTH);
    const [bodyWidth, setBodyWidth] = useState(0);
    const bodyRef = useRef<HTMLDivElement>(null);
    usePageTitle(`${formId === "new" || !formId ? t("New service form") : displayTitle} · ${t("Service Builder")}`, { enabled: translationsReady });
    const layoutAppliedRef = useRef(false);
    const layoutQueryKey = queryKeys.records.lookup("sys_user_preference", "form-builder-layout", LAYOUT_TABLE, LAYOUT_PREFERENCE_KEY, user?.id ?? null);
    const layoutPreferenceQuery = useQuery({
        queryKey: layoutQueryKey,
        enabled: Boolean(user?.id),
        queryFn: async () => {
            const response = await new RecordManagement("sys_user_preference")
                .where("table_name", LAYOUT_TABLE)
                .where("user_id", user?.id)
                .where("preference_key", LAYOUT_PREFERENCE_KEY)
                .first();

            if (!response?.preference_value) return null;
            return typeof response.preference_value === "string" ? JSON.parse(response.preference_value) as BuilderLayoutPreference : response.preference_value as BuilderLayoutPreference;
        },
    });
    const saveLayoutPreference = useMutation({
        mutationFn: async (preference: BuilderLayoutPreference) => {
            if (!user?.id) return;
            const payload = JSON.stringify(preference);
            const existing = await new RecordManagement("sys_user_preference")
                .where("table_name", LAYOUT_TABLE)
                .where("user_id", user.id)
                .where("preference_key", LAYOUT_PREFERENCE_KEY)
                .first();

            if (existing?.id) {
                await new RecordManagement("sys_user_preference").update(existing.id, { preference_value: payload });
                return;
            }

            await new RecordManagement("sys_user_preference").save({
                user_id: user.id,
                table_name: LAYOUT_TABLE,
                preference_key: LAYOUT_PREFERENCE_KEY,
                preference_value: payload,
            });
        },
        onSuccess: (_, preference) => queryClient.setQueryData(layoutQueryKey, preference),
    });

    const targetColumns = useTargetColumns(state.targetTable);
    const { data: tables = [] } = useTables();
    const { data: allColumns = [] } = useColumns();
    const [developerTab, setDeveloperTab] = useState<"html" | "script">("html");
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saveSuccessOpen, setSaveSuccessOpen] = useState(false);
    const [previewVisible, setPreviewVisible] = useState(true);
    useEffect(() => {
        const body = bodyRef.current;
        if (!body) return;
        const update = () => setBodyWidth(body.clientWidth);
        update();
        if (typeof ResizeObserver !== "undefined") {
            const observer = new ResizeObserver(update);
            observer.observe(body);
            return () => observer.disconnect();
        }
        window.addEventListener("resize", update);
        return () => window.removeEventListener("resize", update);
    }, []);
    const [titleError, setTitleError] = useState<string | null>(null);
    const [titleDraftDirty, setTitleDraftDirty] = useState(false);
    const titleDraftRef = useRef<string | null>(null);
    const titleInputRef = useRef<HTMLInputElement>(null);
    const titleFlushRef = useRef<(() => Promise<boolean>) | null>(null);
    const confirmedTitleRef = useRef<{ title: string; displayTitle?: string } | null>(null);
    const savedSnapshotRef = useRef(persistentSnapshot(state));
    const bypassBlockerRef = useRef(false);
    const dirty = persistentSnapshot(state) !== savedSnapshotRef.current || titleDraftDirty;
    const blocker = useBlocker(({ currentLocation, nextLocation }) =>
        !bypassBlockerRef.current && dirty &&
        (currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search)
    );
    useEffect(() => {
        if (!dirty) return;
        const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ""; };
        window.addEventListener("beforeunload", warn);
        return () => window.removeEventListener("beforeunload", warn);
    }, [dirty]);
    const registerTitleFlush = useCallback((flush: () => Promise<boolean>) => {
        titleFlushRef.current = flush;
        return () => { if (titleFlushRef.current === flush) titleFlushRef.current = null; };
    }, []);
    const handleTitleChange = (title: string, displayTitle?: string) => {
        confirmedTitleRef.current = { title, displayTitle };
        actions.setFormTitle(title, displayTitle);
    };
    const handleTitleDraftChange = (draft: string) => {
        titleDraftRef.current = draft;
        if (draft.trim()) setTitleError(null);
    };


    useEffect(() => {
        if (layoutAppliedRef.current || layoutPreferenceQuery.isLoading) return;
        const preference = layoutPreferenceQuery.data;
        if (preference?.leftSidebarWidth) {
            setLeftSidebarWidth(clamp(Number(preference.leftSidebarWidth), MIN_LEFT_WIDTH, MAX_LEFT_WIDTH));
        }
        if (preference?.rightSidebarWidth) {
            setRightSidebarWidth(clamp(Number(preference.rightSidebarWidth), MIN_RIGHT_WIDTH, MAX_RIGHT_WIDTH));
        }
        layoutAppliedRef.current = true;
    }, [layoutPreferenceQuery.data, layoutPreferenceQuery.isLoading]);

    const persistLayoutPreference = (leftWidth: number, rightWidth: number) => {
        saveLayoutPreference.mutate({ leftSidebarWidth: leftWidth, rightSidebarWidth: rightWidth });
    };

    const availableSidebarWidth = bodyWidth > 0 ? Math.max(0, bodyWidth - MIN_CANVAS_WIDTH - HANDLE_WIDTH * (previewVisible ? 3 : 2)) : Infinity;
    const rightMultiplier = previewVisible ? 2 : 1;
    const fitWidths = (requestedLeft: number, requestedRight: number) => {
        let left = requestedLeft;
        let right = requestedRight;
        const excess = Math.max(0, left + right * rightMultiplier - availableSidebarWidth);
        right = Math.max(MIN_FIT_RIGHT_WIDTH, right - excess / rightMultiplier);
        left = Math.max(MIN_FIT_LEFT_WIDTH, Math.min(left, availableSidebarWidth - right * rightMultiplier));
        right = Math.min(right, (availableSidebarWidth - left) / rightMultiplier);
        return { left: Math.round(left), right: Math.round(right) };
    };
    const fitted = fitWidths(leftSidebarWidth, rightSidebarWidth);
    const resizeLimit = (side: "left" | "right") => side === "left"
        ? Math.max(MIN_FIT_LEFT_WIDTH, Math.min(MAX_LEFT_WIDTH, availableSidebarWidth - MIN_FIT_RIGHT_WIDTH * rightMultiplier))
        : Math.max(MIN_FIT_RIGHT_WIDTH, Math.min(MAX_RIGHT_WIDTH, (availableSidebarWidth - MIN_FIT_LEFT_WIDTH) / rightMultiplier));
    const resizeWidths = (side: "left" | "right", requestedWidth: number, initialLeft = fitted.left, initialRight = fitted.right) => {
        if (side === "left") {
            let left = clamp(requestedWidth, MIN_FIT_LEFT_WIDTH, resizeLimit("left"));
            const right = Math.max(MIN_FIT_RIGHT_WIDTH, initialRight - Math.max(0, left + initialRight * rightMultiplier - availableSidebarWidth) / rightMultiplier);
            left = Math.min(left, availableSidebarWidth - right * rightMultiplier);
            return { left: Math.round(left), right: Math.round(right) };
        }
        let right = clamp(requestedWidth, MIN_FIT_RIGHT_WIDTH, resizeLimit("right"));
        const left = Math.max(MIN_FIT_LEFT_WIDTH, initialLeft - Math.max(0, initialLeft + right * rightMultiplier - availableSidebarWidth));
        right = Math.min(right, (availableSidebarWidth - left) / rightMultiplier);
        return { left: Math.round(left), right: Math.round(right) };
    };
    const resizeByKey = (side: "left" | "right", event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
        event.preventDefault();
        const delta = event.key === "ArrowRight" ? 16 : -16;
        const { left, right } = resizeWidths(side, side === "left" ? fitted.left + delta : fitted.right - delta);
        setLeftSidebarWidth(left);
        setRightSidebarWidth(right);
        persistLayoutPreference(left, right);
    };
    const resizeHandle = (side: "left" | "right", key: "left" | "properties" | "preview" | "developer-preview") => <Box key={key}
        className={`service-builder__resize-handle service-builder__resize-handle--${side}`}
        role="separator" aria-orientation="vertical" aria-label={String(t(key === "left" ? "Resize field palette" : key === "properties" ? "Resize properties panel" : "Resize preview"))}
        aria-valuenow={side === "left" ? fitted.left : fitted.right}
        aria-valuemin={side === "left" ? MIN_FIT_LEFT_WIDTH : MIN_FIT_RIGHT_WIDTH}
        aria-valuemax={resizeLimit(side)} tabIndex={0}
        onMouseDown={(event) => startResize(side, event)} onKeyDown={(event) => resizeByKey(side, event)} />;

    const startResize = (side: "left" | "right", event: MouseEvent<HTMLDivElement>) => {
        event.preventDefault();
        const startX = event.clientX;
        const startLeft = fitted.left;
        const startRight = fitted.right;
        let nextLeft = startLeft;
        let nextRight = startRight;

        const handleMove = (moveEvent: globalThis.MouseEvent) => {
            const delta = moveEvent.clientX - startX;
            const resized = resizeWidths(side, side === "left" ? startLeft + delta : startRight - delta, startLeft, startRight);
            nextLeft = resized.left;
            nextRight = resized.right;
            setLeftSidebarWidth(nextLeft);
            setRightSidebarWidth(nextRight);
        };

        const handleUp = () => {
            window.removeEventListener("mousemove", handleMove);
            window.removeEventListener("mouseup", handleUp);
            persistLayoutPreference(nextLeft, nextRight);
        };

        window.addEventListener("mousemove", handleMove);
        window.addEventListener("mouseup", handleUp);
    };

    const buildSyncedStateForSave = (): FormBuilderState => {
        if (state.activeView !== "developer" || !state.htmlDirty) return state;

        const parsed = parseFormHtml(state.htmlSource);
        return {
            ...state,
            fields: parsed.fields || state.fields,
            steps: parsed.steps || state.steps,
            sections: parsed.sections || state.sections,
            targetTable: parsed.targetTable || state.targetTable,
            onSubmit: parsed.onSubmit || state.onSubmit,
            successMessage: parsed.successMessage || state.successMessage,
            successRedirect: parsed.successRedirect || state.successRedirect,
            recordId: parsed.recordId || state.recordId,
            eventsBinding: parsed.eventsBinding || state.eventsBinding,
            activeStepIndex: parsed.steps?.length ? 0 : null,
            htmlDirty: false,
        };
    };

    const handleSave = async () => {
        if (!onSave) return;

        try {
            setSaveError(null);
            const draft = titleDraftRef.current;
            if (draft !== null && !draft.trim()) {
                setTitleError(String(t("Title is required.")));
                titleInputRef.current?.focus();
                return;
            }
            if (!draft && !state.formTitle.trim()) {
                setTitleError(String(t("Title is required.")));
                titleInputRef.current?.focus();
                return;
            }
            if (titleFlushRef.current && !(await titleFlushRef.current())) return;
            const confirmed = confirmedTitleRef.current;
            const saveState = buildSyncedStateForSave();
            if (confirmed) {
                const identityBase = confirmed.displayTitle || confirmed.title;
                saveState.formTitle = confirmed.title;
                saveState.formName = state.identityLocked ? state.formName : normalizeBuilderToken(identityBase, "_");
                saveState.formSlug = state.identityLocked ? state.formSlug : normalizeBuilderToken(identityBase, "-");
            }
            if (state.activeView === "developer" && state.htmlDirty) {
                actions.syncFromHtml();
            }
            bypassBlockerRef.current = true;
            await onSave(saveState);
            savedSnapshotRef.current = persistentSnapshot(saveState);
            setTitleDraftDirty(false);
            setTitleError(null);
            setSaveSuccessOpen(true);
        } catch (error: any) {
            bypassBlockerRef.current = false;
            setSaveError(error?.response?.data?.error?.message || error?.message || String(t("Unable to save form template.")));
        }
    };

    return (
        <Box className="service-builder">
            <BuilderToolbar
                state={state}
                displayTitle={displayTitle}
                saving={saving}
                onTitleChange={handleTitleChange}
                onTitleDraftChange={handleTitleDraftChange}
                onTitleDirtyChange={setTitleDraftDirty}
                registerTitleFlush={registerTitleFlush}
                titleInputRef={titleInputRef}
                titleError={titleError}
                onViewChange={actions.setView}
                previewVisible={previewVisible}
                onAddStep={() => actions.addStep(`Step ${state.steps.length + 1}`)}
                onTogglePreview={() => setPreviewVisible((current) => !current)}
                onSave={onSave ? handleSave : undefined}
            />
            {saveError && <Alert severity="error" onClose={() => setSaveError(null)}>{saveError}</Alert>}
            <Box
                ref={bodyRef}
                className={`service-builder__body${state.activeView === "nocode" && previewVisible ? " service-builder__body--preview-visible" : ""}`}
                style={state.activeView === "nocode" ? {
                    "--service-builder-left-width": `${fitted.left}px`,
                    "--service-builder-right-width": `${fitted.right}px`,
                    "--service-builder-preview-width": `${fitted.right}px`,
                } as CSSProperties : undefined}
            >
                {state.activeView === "nocode" ? (
                    <>
                        <FieldPalette onAddField={(fieldType) => actions.addField(fieldType)} />
                        {resizeHandle("left", "left")}
                        <FormCanvas
                            fields={state.fields}
                            steps={state.steps}
                            selectedField={state.selectedField}
                            activeStepIndex={state.activeStepIndex}
                            onAddField={actions.addField}
                            onSelectField={actions.selectField}
                            onSelectStep={actions.setActiveStep}
                            onRemoveField={actions.removeField}
                            onMoveField={actions.moveField}
                            onRemoveStep={actions.removeStep}
                        />
                        {resizeHandle("right", "properties")}
                        <PropertyPanel
                            state={state}
                            selectedField={selectedFieldDef}
                            columns={targetColumns}
                            tables={tables}
                            allColumns={allColumns}
                            onFormPropsChange={actions.setFormProps}
                            onFieldChange={actions.updateField}
                            onFieldRulesChange={actions.updateFieldRules}
                            onStepChange={actions.updateStep}
                            onPreviewTranslation={actions.setPreviewTranslation}
                        />
                        {previewVisible && resizeHandle("right", "preview")}
                        {previewVisible && <FormPreview html={state.htmlSource} styleCode={state.styleCode} scriptCode={state.scriptCode} draftTranslations={state.previewTranslations} />}
                    </>
                ) : (
                    <Box
                        className={`service-builder__developer-layout${previewVisible ? "" : " service-builder__developer-layout--preview-hidden"}`}
                        style={{ "--service-builder-preview-width": `${rightSidebarWidth}px` } as CSSProperties}
                    >
                        <Box className="service-builder__developer-pane">
                            <Tabs value={developerTab} onChange={(_, value) => setDeveloperTab(value)} className="service-builder__developer-tabs">
                                <Tab value="html" label={t("Template HTML")} />
                                <Tab value="script" label={t("Script Code")} />
                            </Tabs>
                            {developerTab === "html" ? (
                                <HtmlEditor value={state.htmlSource} dirty={state.htmlDirty} onChange={actions.setHtml} onSync={actions.syncFromHtml} />
                            ) : (
                                <ScriptEditor value={state.scriptCode} fields={state.fields} onChange={actions.setScriptCode} />
                            )}
                        </Box>
                        {previewVisible && resizeHandle("right", "developer-preview")}
                        {previewVisible && <FormPreview html={state.htmlSource} styleCode={state.styleCode} scriptCode={state.scriptCode} draftTranslations={state.previewTranslations} />}
                    </Box>
                )}
            </Box>
            <Box className="service-builder__status">{displayTitle || t("New service form")}</Box>
            <Dialog open={blocker.state === "blocked"} onClose={() => blocker.reset?.()} fullWidth maxWidth="xs">
                <DialogTitle>{t("Unsaved changes")}</DialogTitle>
                <DialogContent><DialogContentText>{t("You have unsaved changes. Discard them and leave?")}</DialogContentText></DialogContent>
                <DialogActions>
                    <Button onClick={() => blocker.reset?.()} color="inherit">{t("Stay")}</Button>
                    <Button onClick={() => blocker.proceed?.()} variant="contained" color="error">{t("Discard")}</Button>
                </DialogActions>
            </Dialog>
            <Snackbar open={saveSuccessOpen} autoHideDuration={2400} onClose={() => setSaveSuccessOpen(false)}>
                <Alert severity="success" variant="filled">{t("Form template saved.")}</Alert>
            </Snackbar>
        </Box>
    );
};

export default FormBuilderShell;
